import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { useCompany } from "@/context/CompanyContext";
import { api, CATEGORY_LABELS, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SEG_LABELS = { supplier: "Fournisseur", year: "Année", month: "Mois", department: "Service" };
const EXAMPLE = { supplier: "Michelin", year: "2026", month: "03 - Mars", department: "Comptabilité" };
const NONE = "none";

const preview = (r) => [r.root, ...r.segments.map((s) => EXAMPLE[s])].filter(Boolean).join(" / ") || "Divers";

function RuleRow({ type, rule, def, onChange }) {
  const slots = [0, 1, 2];
  if (!rule) {
    return (
      <div className="grid md:grid-cols-[160px_1fr] gap-2 md:items-center py-3 border-b last:border-0" data-testid={`rule-row-${type}`}>
        <div className="text-sm font-semibold">{CATEGORY_LABELS[type] || type}</div>
        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
          <span className="inline-flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-[#1E3A8A]" />L'IA choisit ou crée le dossier</span>
          <Button size="sm" variant="outline" onClick={() => onChange(def)} data-testid={`rule-enable-${type}`}>Utiliser une règle fixe</Button>
        </div>
      </div>
    );
  }
  const setSeg = (i, v) => {
    const segs = [...rule.segments];
    if (v === NONE) segs.splice(i, 1); else segs[i] = v;
    onChange({ ...rule, segments: segs.filter(Boolean) });
  };
  return (
    <div className="grid md:grid-cols-[160px_1fr] gap-2 md:items-center py-3 border-b last:border-0" data-testid={`rule-row-${type}`}>
      <div className="text-sm font-semibold">{CATEGORY_LABELS[type] || type}</div>
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <Input value={rule.root} onChange={(e) => onChange({ ...rule, root: e.target.value })} placeholder="Dossier principal"
            className="w-full sm:w-48 h-9" data-testid={`rule-root-${type}`} />
          {slots.map((i) => (i <= rule.segments.length) && (
            <div key={i} className="flex items-center gap-2">
              <span className="text-slate-400">/</span>
              <Select value={rule.segments[i] || NONE} onValueChange={(v) => setSeg(i, v)}>
                <SelectTrigger className="w-36 h-9 bg-white" data-testid={`rule-seg-${type}-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— aucun —</SelectItem>
                  {Object.keys(SEG_LABELS).filter((s) => s === rule.segments[i] || !rule.segments.includes(s))
                    .map((s) => <SelectItem key={s} value={s}>{SEG_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <div className="text-xs text-slate-500 flex flex-wrap items-center gap-3" data-testid={`rule-preview-${type}`}>Exemple : {preview(rule)}
          <button type="button" className="text-[#1E3A8A] hover:underline" onClick={() => onChange(null)} data-testid={`rule-ai-${type}`}>Laisser l'IA décider</button>
        </div>
      </div>
    </div>
  );
}

export function AdminClassifyRules() {
  const [cfg, setCfg] = useState(null);
  const [deps, setDeps] = useState("");
  const [busy, setBusy] = useState(false);
  const { company } = useCompany();
  useEffect(() => {
    api.get("/admin/classify-rules").then((r) => { setCfg(r.data); setDeps(r.data.departments.join(", ")); }).catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!cfg) return <Loader2 className="h-5 w-5 animate-spin" />;
  const setRule = (type, rule) => {
    const rules = { ...cfg.rules };
    if (rule) rules[type] = rule; else delete rules[type];
    setCfg({ ...cfg, rules });
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/classify-rules", { rules: cfg.rules, departments: deps.split(",").map((d) => d.trim()).filter(Boolean) });
      setCfg({ ...cfg, ...data }); setDeps(data.departments.join(", "));
      toast.success("Règles de classement enregistrées");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="classify-rules">
      <div className="text-sm font-semibold">Règles de classement · {company?.name}</div>
      <p className="text-sm text-slate-600">Chaque société a ses propres règles (changez de société en haut de l'écran). Pour chaque type de document, choisissez le dossier principal puis jusqu'à 3 sous-dossiers, ou laissez l'IA décider : elle réutilise les dossiers existants et invente une arborescence adaptée (ex. SCI : Baux / Locataire / Année). Les documents de type « Autre » sont toujours rangés par l'IA.</p>
      <div className="border rounded-lg px-4">
        {Object.keys(cfg.defaults).map((t) => <RuleRow key={t} type={t} rule={cfg.rules[t]} def={cfg.defaults[t]} onChange={(r) => setRule(t, r)} />)}
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Services de l'entreprise (séparés par des virgules)</label>
        <Input value={deps} onChange={(e) => setDeps(e.target.value)} className="mt-1" data-testid="departments-input" />
        <p className="text-xs text-slate-500 mt-1">L'IA choisit le service concerné parmi cette liste lors de l'analyse de chaque nouveau document.</p>
      </div>
      <div className="flex gap-2">
        <Button onClick={save} disabled={busy} data-testid="classify-rules-save">Enregistrer les règles</Button>
        <Button variant="ghost" onClick={() => { setCfg({ ...cfg, rules: cfg.defaults }); setDeps(cfg.default_departments.join(", ")); }} data-testid="classify-rules-reset">Revenir aux règles par défaut</Button>
      </div>
    </div>
  );
}
